import { useState, useRef } from "react";

// ─── THEME TOKENS ────────────────────────────────────────────────────────────
const T = {
  bg:       "#0B0F1A",
  surface:  "#111827",
  card:     "#1A2235",
  border:   "#1E2D45",
  accent:   "#38BDF8",
  accentDim:"#0EA5E9",
  green:    "#34D399",
  yellow:   "#FCD34D",
  red:      "#F87171",
  purple:   "#A78BFA",
  orange:   "#FB923C",
  textPri:  "#F0F6FF",
  textSec:  "#94A3B8",
  textMute: "#4B5563",
  codeBg:   "#060D18",
  codeStr:  "#86EFAC",
  codeKw:   "#7DD3FC",
  codeFn:   "#FDE68A",
  codeCmt:  "#475569",
  codeNum:  "#F9A8D4",
};

// ─── SYNTAX HIGHLIGHT (simple tokenizer) ─────────────────────────────────────
function highlight(code) {
  const lines = code.split("\n");
  return lines.map((line, li) => {
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i] === "#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i = line.length; continue;
      }
      if (line[i] === '"' || line[i] === "'") {
        const q = line[i]; let j = i+1;
        while (j < line.length && !(line[j]===q && line[j-1]!=="\\")) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i = j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j = i;
        while (j<line.length && /[\d._]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i = j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j = i;
        while (j<line.length && /\w/.test(line[j])) j++;
        const word = line.slice(i,j);
        const kws = ["import","from","as","def","class","return","for","in","if","else","elif","and","or","not","True","False","None","lambda","with","print","pass","break","continue","yield","async","await"];
        const blt = ["pd","np","df","df2","df3","df4","df5","result","chunk","stacked","merged","inner","left","outer"];
        let color = T.textPri;
        if (kws.includes(word)) color = T.codeKw;
        else if (blt.includes(word)) color = T.accent;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i = j; continue;
      }
      const ops = "()[]{}=<>+-*/%.,!:";
      if (ops.includes(line[i])) {
        tokens.push(<span key={i} style={{color:"#93C5FD"}}>{line[i]}</span>);
        i++; continue;
      }
      tokens.push(<span key={i} style={{color:T.textPri}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

// ─── REUSABLE COMPONENTS ──────────────────────────────────────────────────────
function CodeBlock({ code, label = "python" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"6px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",
            padding:"2px 8px",borderRadius:4,transition:"color .2s"}}>
          {copied ? "✓ copied" : "copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"14px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function OutputBox({ children }) {
  return (
    <div style={{background:"#050C17",border:"1px solid #1E3A52",borderRadius:8,
      padding:"12px 16px",marginBottom:16,fontFamily:"monospace",fontSize:12,
      color:T.green,lineHeight:1.65,whiteSpace:"pre-wrap",overflowX:"auto"}}>
      {children}
    </div>
  );
}

function Concept({ children }) {
  return (
    <div style={{background:T.card,borderLeft:`3px solid ${T.accent}`,borderRadius:"0 8px 8px 0",
      padding:"12px 16px",marginBottom:14,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      {children}
    </div>
  );
}

function Analogy({ children }) {
  return (
    <div style={{background:"#0F1E2E",border:"1px solid #1E3A52",borderRadius:8,
      padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.accent,letterSpacing:1,marginBottom:6,textTransform:"uppercase"}}>
        Analogy
      </div>
      <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{children}</div>
    </div>
  );
}

function ASCII({ children }) {
  return (
    <pre style={{background:"#07111E",border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:14,fontSize:12,color:"#7DD3FC",
      lineHeight:1.6,overflowX:"auto",fontFamily:"'JetBrains Mono','Fira Mono',monospace"}}>
      {children}
    </pre>
  );
}

function WarnBox({ items }) {
  return (
    <div style={{background:"#1C1506",border:"1px solid #78350F",borderRadius:8,
      padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.yellow,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>
        Common Mistakes
      </div>
      {items.map((it,i)=>(
        <div key={i} style={{fontSize:13,color:"#D4B483",marginBottom:5,lineHeight:1.5}}>{it}</div>
      ))}
    </div>
  );
}

function InfoBox({ title, color="#38BDF8", bgColor="#071929", borderColor="#1E3A52", children }) {
  return (
    <div style={{background:bgColor,border:`1px solid ${borderColor}`,borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>{title}</div>
      <div style={{fontSize:13,color:T.textSec,lineHeight:1.7}}>{children}</div>
    </div>
  );
}

function InterviewQ({ items }) {
  const [open, setOpen] = useState({});
  return (
    <div style={{background:"#071929",border:"1px solid #1E3A52",borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:"#60A5FA",letterSpacing:1,marginBottom:10,textTransform:"uppercase"}}>
        Interview Questions
      </div>
      {items.map((item,i)=>(
        <div key={i} style={{marginBottom:8}}>
          <div onClick={()=>setOpen(o=>({...o,[i]:!o[i]}))}
            style={{cursor:"pointer",display:"flex",gap:8,alignItems:"flex-start",
              fontSize:13,color:T.textPri,fontWeight:500,lineHeight:1.5}}>
            <span style={{color:"#60A5FA",minWidth:16,marginTop:1}}>{open[i]?"▾":"▸"}</span>
            <span>{item.q}</span>
          </div>
          {open[i] && (
            <div style={{fontSize:12.5,color:T.textSec,marginTop:6,paddingLeft:24,lineHeight:1.65}}>{item.a}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function Exercises({ items }) {
  const [done, setDone] = useState({});
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.purple,letterSpacing:1,marginBottom:10,textTransform:"uppercase",
        display:"flex",alignItems:"center",gap:6}}>
        <span>Hands-on Exercises</span>
        <span style={{fontSize:10,color:T.textMute}}>({Object.values(done).filter(Boolean).length}/{items.length} done)</span>
      </div>
      {items.map((item,i)=>(
        <div key={i} onClick={()=>setDone(d=>({...d,[i]:!d[i]}))}
          style={{display:"flex",gap:10,marginBottom:8,cursor:"pointer",alignItems:"flex-start"}}>
          <span style={{color:done[i]?T.green:T.textMute,fontSize:14,minWidth:16,marginTop:1}}>
            {done[i]?"✓":"○"}
          </span>
          <span style={{fontSize:13,color:done[i]?T.textMute:T.textSec,
            textDecoration:done[i]?"line-through":"none",lineHeight:1.5}}>
            {item}
          </span>
        </div>
      ))}
    </div>
  );
}

function SQLCompare({ pandas, sql }) {
  return (
    <div style={{background:"#060D18",border:`1px solid ${T.border}`,borderRadius:8,padding:"14px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.orange,letterSpacing:1,marginBottom:10,textTransform:"uppercase"}}>
        SQL ↔ Pandas Comparison
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div>
          <div style={{fontSize:10,color:T.accent,marginBottom:6,fontWeight:600}}>Pandas</div>
          <pre style={{fontFamily:"monospace",fontSize:12,color:"#86EFAC",lineHeight:1.6,margin:0,whiteSpace:"pre-wrap"}}>{pandas}</pre>
        </div>
        <div>
          <div style={{fontSize:10,color:T.yellow,marginBottom:6,fontWeight:600}}>SQL</div>
          <pre style={{fontFamily:"monospace",fontSize:12,color:"#FDE68A",lineHeight:1.6,margin:0,whiteSpace:"pre-wrap"}}>{sql}</pre>
        </div>
      </div>
    </div>
  );
}

function PerfBox({ children }) {
  return (
    <div style={{background:"#0C1A0E",border:"1px solid #14532D",borderRadius:8,padding:"12px 16px",marginBottom:14}}>
      <div style={{fontSize:10,fontWeight:600,color:T.green,letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>
        Performance / Best Practice
      </div>
      <div style={{fontSize:13,color:"#A7F3D0",lineHeight:1.7}}>{children}</div>
    </div>
  );
}

function UseCaseGrid({ items }) {
  const colors = [T.accent,T.green,T.purple,T.orange,T.yellow,T.red];
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
      {items.map((item,i)=>(
        <div key={i} style={{background:T.card,borderRadius:8,padding:"10px 12px",
          borderLeft:`2px solid ${colors[i%colors.length]}`}}>
          <div style={{fontSize:10,fontWeight:600,color:colors[i%colors.length],marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>
            {item.label}
          </div>
          <div style={{fontSize:12.5,color:T.textSec,lineHeight:1.5}}>{item.text}</div>
        </div>
      ))}
    </div>
  );
}

// ─── TABS COMPONENT ───────────────────────────────────────────────────────────
function Tabs({ tabs }) {
  const [active, setActive] = useState(0);
  return (
    <div>
      <div style={{display:"flex",gap:4,borderBottom:`1px solid ${T.border}`,marginBottom:20,flexWrap:"wrap"}}>
        {tabs.map((tab,i)=>(
          <button key={i} onClick={()=>setActive(i)}
            style={{padding:"8px 14px",fontSize:12.5,cursor:"pointer",border:"none",
              background:"none",color:active===i?T.accent:T.textSec,
              borderBottom:active===i?`2px solid ${T.accent}`:"2px solid transparent",
              marginBottom:-1,fontWeight:active===i?600:400,transition:"color .15s",
              fontFamily:"inherit",whiteSpace:"nowrap"}}>
            {tab.label}
          </button>
        ))}
      </div>
      {tabs[active].content}
    </div>
  );
}

// ─── OUTPUT EXPLAIN ───────────────────────────────────────────────────────────
function OutputLine({ children }) {
  return (
    <div style={{display:"flex",gap:8,marginBottom:6,fontSize:13,lineHeight:1.5}}>
      <span style={{color:T.accent,minWidth:16}}>→</span>
      <span style={{color:T.textSec}}>{children}</span>
    </div>
  );
}

// ─── SECTION 1: DATAFRAME FUNDAMENTALS ───────────────────────────────────────
function S1() {
  return (
    <Tabs tabs={[
      {
        label:"What is Pandas?",
        content:(
          <div>
            <Concept>
              <strong style={{color:T.textPri}}>Pandas</strong> is a Python library for data manipulation and analysis.
              It provides two primary data structures: <code style={{color:T.accent}}>Series</code> (1D) and{" "}
              <code style={{color:T.accent}}>DataFrame</code> (2D table). Built on NumPy, it is the backbone of
              data science in Python — and the standard format for feeding data into ML models and analytics pipelines.
            </Concept>
            <Analogy>
              Think of Pandas as a super-powered Excel that runs inside Python. You can filter rows, compute
              statistics, join tables, parse logs, handle missing data — all with concise, fast, reproducible
              code instead of clicking around a spreadsheet.
            </Analogy>
            <ASCII>{`Python Data Stack:
┌─────────────────────────────────────────┐
│  NumPy  — arrays, vectorized math       │  ← foundation
├─────────────────────────────────────────┤
│  Pandas — DataFrames, Series            │  ← your daily driver
├─────────────────────────────────────────┤
│  Matplotlib / Seaborn — charts          │  ← visualize results
├─────────────────────────────────────────┤
│  Scikit-learn / TensorFlow — ML models  │  ← feed DataFrames to ML
└─────────────────────────────────────────┘`}</ASCII>
            <CodeBlock code={`import pandas as pd
import numpy as np

print(pd.__version__)      # e.g., 2.2.0
print(pd.show_versions())  # full environment diagnostics`} />
            <UseCaseGrid items={[
              {label:"Log Analytics", text:"Parse millions of Kubernetes pod logs into a DataFrame for pattern analysis"},
              {label:"Prometheus", text:"Load scraped metrics into DataFrames to compute SLO compliance rates"},
              {label:"AI/ML", text:"Feature engineering pipelines use DataFrames as the standard input format"},
              {label:"Cloud Ops", text:"Analyze AWS Cost Explorer CSV exports to spot cost anomalies per service"},
            ]} />
          </div>
        )
      },
      {
        label:"DataFrame vs Series",
        content:(
          <div>
            <Concept>
              A <strong style={{color:T.textPri}}>Series</strong> is a 1-dimensional labeled array — a single column.
              A <strong style={{color:T.textPri}}>DataFrame</strong> is a 2-dimensional table — a collection of Series
              sharing the same index. Every column in a DataFrame is a Series.
            </Concept>
            <ASCII>{`SERIES (1D):                  DATAFRAME (2D):
Index │ Value              Index │ pod       │ cpu   │ memory
──────┼────────            ──────┼───────────┼───────┼───────
  0   │ "nginx"              0   │ nginx     │ 12.3  │  256
  1   │ "redis"              1   │ redis     │  8.1  │  128
  2   │ "kafka"              2   │ kafka     │ 45.6  │  512
                                    ↑ column ← each column is a Series`}</ASCII>
            <CodeBlock code={`import pandas as pd

# Series — a single labeled column
pods = pd.Series(["nginx", "redis", "kafka"], name="pod_name")
print(type(pods))   # <class 'pandas.core.series.Series'>
print(pods.shape)   # (3,)

# DataFrame — multiple columns
df = pd.DataFrame({
    "pod":    ["nginx", "redis", "kafka"],
    "cpu":    [12.3, 8.1, 45.6],
    "memory": [256, 128, 512]
})
print(type(df))     # <class 'pandas.core.frame.DataFrame'>
print(df.shape)     # (3, 3)  → rows × columns

# Extracting a column returns a Series
cpu_series = df["cpu"]
print(type(cpu_series))  # pandas.core.series.Series`} />
            <OutputBox>{`<class 'pandas.core.series.Series'>
(3,)
<class 'pandas.core.frame.DataFrame'>
(3, 3)
<class 'pandas.core.series.Series'>`}</OutputBox>
            <OutputLine>A Series has shape (3,) — one dimension, length 3.</OutputLine>
            <OutputLine>A DataFrame has shape (3, 3) — 3 rows × 3 columns.</OutputLine>
            <OutputLine>Accessing a single column with df["cpu"] returns a Series.</OutputLine>
          </div>
        )
      },
      {
        label:"Rows, Columns, Index",
        content:(
          <div>
            <Concept>
              Every DataFrame has: <strong style={{color:T.textPri}}>Rows</strong> (records),{" "}
              <strong style={{color:T.textPri}}>Columns</strong> (features), and an{" "}
              <strong style={{color:T.textPri}}>Index</strong> (row labels, default 0,1,2…).
              The index can be timestamps, hostnames, or any unique identifier.
            </Concept>
            <ASCII>{`             COLUMNS
             ┌──────────┬───────┬────────┐
             │   pod    │  cpu  │ memory │
INDEX  ──────┼──────────┼───────┼────────┤
  0    ──▶   │  nginx   │ 12.3  │  256   │  ← ROW 0
  1    ──▶   │  redis   │  8.1  │  128   │  ← ROW 1
  2    ──▶   │  kafka   │ 45.6  │  512   │  ← ROW 2
             └──────────┴───────┴────────┘
  ↑
  Default RangeIndex — can be set to hostname, timestamp, etc.`}</ASCII>
            <CodeBlock code={`df = pd.DataFrame({
    "pod":    ["nginx", "redis", "kafka"],
    "cpu":    [12.3, 8.1, 45.6],
    "memory": [256, 128, 512]
})

print(df.index)    # RangeIndex(start=0, stop=3, step=1)
print(df.columns)  # Index(['pod', 'cpu', 'memory'], dtype='object')

# Set a custom index (e.g., pod name as row label)
df2 = df.set_index("pod")
print(df2)

# Reset index back to default integers
df3 = df2.reset_index()
print(df3.columns.tolist())`} />
            <OutputBox>{`RangeIndex(start=0, stop=3, step=1)
Index(['pod', 'cpu', 'memory'], dtype='object')

         cpu  memory
pod
nginx   12.3     256
redis    8.1     128
kafka   45.6     512

['pod', 'cpu', 'memory']`}</OutputBox>
            <InterviewQ items={[
              {q:"What is the default index in a DataFrame?", a:"A RangeIndex starting at 0, incrementing by 1. Replace it with set_index() to use meaningful labels like pod names or timestamps."},
              {q:"What happens when you set a column as index?", a:"That column is removed from columns and becomes the row label. Data is never lost — reset_index() brings it back as a column."},
            ]} />
          </div>
        )
      },
      {
        label:"Creating DataFrames",
        content:(
          <div>
            <Concept>DataFrames can be created from dictionaries, lists, CSV, JSON, SQL queries, and more.</Concept>
            <CodeBlock code={`import pandas as pd, io, json

# 1. From a Dictionary (most common)
df1 = pd.DataFrame({
    "pod":    ["nginx", "redis", "kafka"],
    "cpu":    [12.3, 8.1, 45.6],
    "status": ["running", "running", "pending"]
})

# 2. From List of Dicts (JSON / Kubernetes API response)
records = [
    {"cluster": "mh-01", "nodes": 3, "region": "maharashtra"},
    {"cluster": "dl-01", "nodes": 5, "region": "delhi"},
    {"cluster": "tn-01", "nodes": 4, "region": "tamilnadu"},
]
df2 = pd.DataFrame(records)

# 3. From CSV string / file
csv_data = """pod,cpu,memory
nginx,12.3,256
redis,8.1,128
kafka,45.6,512"""
df3 = pd.read_csv(io.StringIO(csv_data))

# 4. From JSON
json_str = '[{"pod":"nginx","cpu":12.3},{"pod":"redis","cpu":8.1}]'
df4 = pd.read_json(io.StringIO(json_str))

# 5. From SQL (using SQLAlchemy or sqlite3)
# df = pd.read_sql("SELECT * FROM metrics WHERE cpu > 80", conn)

print(df1)
print()
print(df2)`} />
            <OutputBox>{`    pod   cpu   status
0  nginx  12.3  running
1  redis   8.1  running
2  kafka  45.6  pending

  cluster  nodes       region
0   mh-01      3  maharashtra
1   dl-01      5        delhi
2   tn-01      4    tamilnadu`}</OutputBox>
            <WarnBox items={[
              "❌ Dictionary keys of unequal length → ValueError: All arrays must be of the same length",
              "❌ pd.read_csv() without checking sep= (some CSVs use tab or semicolon as delimiter)",
              "✅ Always check df.shape after loading to verify row/column counts match expectations",
            ]} />
            <Exercises items={[
              "Create a DataFrame of 5 OCP clusters: cluster_name, node_count, zone, cpu_total, memory_gb",
              "Create the same DataFrame from a list of dicts (simulating a Kubernetes API JSON response)",
              "Set cluster_name as the index. Reset it back and verify columns are unchanged",
              "Read a CSV with parse_dates=['timestamp'] and confirm the dtype of that column",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 2: EXPLORATION ───────────────────────────────────────────────────
function S2() {
  return (
    <Tabs tabs={[
      {
        label:"head() & tail()",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>head(n)</code> returns the first n rows (default 5).{" "}
              <code style={{color:T.accent}}>tail(n)</code> returns the last n rows.
              Always run these first when loading any dataset.
            </Concept>
            <Analogy>Like opening a book to the first and last few pages — you get the structure and tone before committing to reading the whole thing.</Analogy>
            <CodeBlock code={`import pandas as pd, numpy as np

df = pd.DataFrame({
    "cluster":    ["mh-01","mh-02","dl-01","dl-02","tn-01"],
    "cpu_pct":    [72.3, 85.1, 45.6, 91.2, 60.0],
    "memory_pct": [65.0, 88.5, 50.2, 95.0, 71.3],
    "alerts":     [2, 5, 0, 8, 1],
    "status":     ["ok","warn","ok","crit","ok"],
})

print(df.head(3))   # first 3 rows
print("---")
print(df.tail(2))   # last 2 rows`} />
            <OutputBox>{`  cluster  cpu_pct  memory_pct  alerts status
0   mh-01     72.3        65.0       2     ok
1   mh-02     85.1        88.5       5   warn
2   dl-01     45.6        50.2       0     ok
---
  cluster  cpu_pct  memory_pct  alerts status
3   dl-02     91.2        95.0       8   crit
4   tn-01     60.0        71.3       1     ok`}</OutputBox>
          </div>
        )
      },
      {
        label:"info() & describe()",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>info()</code> shows column names, non-null counts, and dtypes.{" "}
              <code style={{color:T.accent}}>describe()</code> computes statistics for numeric columns.
              Use both together when first exploring any dataset.
            </Concept>
            <CodeBlock code={`df.info()

print(df.describe())

# describe() for string (object) columns too
print(df.describe(include="object"))

# Custom percentiles — useful for SLO analysis
print(df.describe(percentiles=[.50, .90, .95, .99]))`} />
            <OutputBox>{`<class 'pandas.core.frame.DataFrame'>
RangeIndex: 5 entries, 0 to 4
Data columns (total 5 columns):
 #   Column      Non-Null Count  Dtype
---  ------      --------------  -----
 0   cluster     5 non-null      object
 1   cpu_pct     5 non-null      float64
 2   memory_pct  5 non-null      float64
 3   alerts      5 non-null      int64
 4   status      5 non-null      object

       cpu_pct  memory_pct  alerts
count    5.000       5.000   5.000
mean    70.840      74.000   3.200
std     18.190      18.180   3.110
min     45.600      50.200   0.000
50%     72.300      71.300   2.000
90%     88.700      92.300   7.200
95%     89.900      93.700   7.600
99%     90.980      94.620   7.920
max     91.200      95.000   8.000`}</OutputBox>
            <OutputLine>info() reveals zero null values — all 5 rows have data in every column.</OutputLine>
            <OutputLine>mean cpu_pct = 70.84% is the baseline. std = 18.19 shows spread — some clusters run hot.</OutputLine>
            <OutputLine>P90 cpu_pct = 88.7% — only 10% of clusters exceed this. Useful as a Prometheus alert threshold.</OutputLine>
            <InterviewQ items={[
              {q:"What is the difference between info() and describe()?", a:"info() gives schema-level metadata: dtypes, null counts, memory usage. describe() gives statistical summaries: mean, std, percentiles. Use both together when first exploring any dataset."},
              {q:"How do you get statistics for string columns?", a:"Pass include='object' or include='all' to describe(). For strings it shows count, unique, top (most frequent), and freq (frequency of top value)."},
            ]} />
          </div>
        )
      },
      {
        label:"shape, dtypes, memory",
        content:(
          <div>
            <CodeBlock code={`print("Shape:", df.shape)          # (rows, cols)
print("Rows:", df.shape[0])
print("Cols:", df.shape[1])
print()
print("Columns:", df.columns.tolist())
print()
print("Dtypes:")
print(df.dtypes)

# Memory usage per column (bytes)
print(df.memory_usage(deep=True))

total_mb = df.memory_usage(deep=True).sum() / 1024**2
print(f"Total: {total_mb:.4f} MB")

# Float64 vs Float32 at scale (1M rows)
rows = 1_000_000
f64 = rows * 8 / 1024**2
f32 = rows * 4 / 1024**2
print(f"1M rows float64: {f64:.1f} MB")
print(f"1M rows float32: {f32:.1f} MB  ← 50% saving")`} />
            <OutputBox>{`Shape: (5, 5)
Rows: 5
Cols: 5

Columns: ['cluster', 'cpu_pct', 'memory_pct', 'alerts', 'status']

Dtypes:
cluster       object
cpu_pct      float64
memory_pct   float64
alerts         int64
status        object

Index           128
cluster         337
cpu_pct          40
memory_pct       40
alerts           40
status          307
Total: 0.0009 MB

1M rows float64: 7.6 MB
1M rows float32: 3.8 MB  ← 50% saving`}</OutputBox>
            <PerfBox>
              Always check dtypes after loading. A numeric column stored as <code style={{color:T.accent}}>object</code> (string)
              will silently fail math operations. Use <code style={{color:T.accent}}>pd.to_numeric()</code> or{" "}
              <code style={{color:T.accent}}>.astype()</code> to fix.
              Use <code style={{color:T.accent}}>deep=True</code> in memory_usage() — without it, object columns
              are severely underreported.
            </PerfBox>
            <Exercises items={[
              "Load a Prometheus metrics CSV and find which column uses the most memory",
              "Use describe(percentiles=[.90,.95,.99]) to find P90/P95/P99 of cpu_pct for SLO analysis",
              "Check how many non-null values exist per column. Identify any with partial nulls",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 3: SELECTING DATA ────────────────────────────────────────────────
function S3() {
  return (
    <Tabs tabs={[
      {
        label:"Columns",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd

df = pd.DataFrame({
    "cluster":   ["mh-01","mh-02","dl-01","dl-02","tn-01"],
    "cpu_pct":   [72.3, 85.1, 45.6, 91.2, 60.0],
    "mem_pct":   [65.0, 88.5, 50.2, 95.0, 71.3],
    "alerts":    [2, 5, 0, 8, 1],
    "status":    ["ok","warn","ok","crit","ok"],
    "region":    ["mh","mh","dl","dl","tn"]
})

# Single column → returns Series
cpu = df["cpu_pct"]
print(type(cpu))

# Multiple columns → returns DataFrame (double brackets!)
subset = df[["cluster","cpu_pct","status"]]
print(subset)

# Dot notation (only for simple names, no spaces)
print(df.region.unique())`} />
            <WarnBox items={[
              "❌ df['cpu_pct','mem_pct'] → KeyError. Use df[['cpu_pct','mem_pct']] (double brackets for multiple columns)",
              "❌ df.my column → SyntaxError. Dot notation fails with spaces or special chars — always use df['my column']",
              "✅ df[['col']] returns a DataFrame with one column. df['col'] returns a Series — know the difference!",
            ]} />
          </div>
        )
      },
      {
        label:"loc & iloc",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>loc</code> selects by <strong style={{color:T.textPri}}>label</strong> (index name, column name).{" "}
              <code style={{color:T.accent}}>iloc</code> selects by <strong style={{color:T.textPri}}>integer position</strong> (0-based, like Python lists).
            </Concept>
            <ASCII>{`loc → LABEL-based                  iloc → POSITION-based
df.loc[0, "cpu_pct"]               df.iloc[0, 1]
df.loc[1:3, "cpu_pct":"alerts"]    df.iloc[1:4, 1:4]
     ↑ inclusive BOTH ends               ↑ exclusive right end (Python-style)`}</ASCII>
            <CodeBlock code={`# loc — by label
print(df.loc[0, "cpu_pct"])                       # 72.3
print(df.loc[1:3, ["cluster","cpu_pct"]])          # rows 1-3 inclusive
print(df.loc[df["status"]=="ok", "cluster"])        # Boolean + label

# iloc — by position (Python list style)
print(df.iloc[0, 1])               # row 0, col index 1
print(df.iloc[1:3, 0:3])           # rows 1-2, cols 0-2 (exclusive end)
print(df.iloc[-1, :])              # last row, all columns

# Custom index + loc
df2 = df.set_index("cluster")
print(df2.loc["mh-01", "cpu_pct"])      # 72.3 — by label
print(df2.loc["mh-01":"dl-01"])         # label slice (inclusive!)`} />
            <OutputBox>{`72.3
  cluster  cpu_pct
1   mh-02     85.1
2   dl-01     45.6
3   dl-02     91.2

0    mh-01
2    dl-01
4    tn-01
Name: cluster, dtype: object`}</OutputBox>
            <InterviewQ items={[
              {q:"What is the key difference between loc and iloc?", a:"loc uses label-based indexing — the actual index values and column names. iloc uses integer position (0-based). Critical: loc slicing is INCLUSIVE on both ends; iloc is EXCLUSIVE on the right end (like Python list slicing)."},
              {q:"When would you use iloc over loc?", a:"When you need the first/last N rows regardless of the index values, or when iterating over batches by position. loc is safer and more readable for named column access in production code."},
            ]} />
          </div>
        )
      },
      {
        label:"Boolean Indexing",
        content:(
          <div>
            <Concept>Boolean indexing uses a True/False mask to filter rows. The mask must be the same length as the DataFrame.</Concept>
            <ASCII>{`df["cpu_pct"] > 70
  row 0 (72.3)  →  True   ✓ included
  row 1 (85.1)  →  True   ✓ included
  row 2 (45.6)  →  False  ✗ excluded
  row 3 (91.2)  →  True   ✓ included
  row 4 (60.0)  →  False  ✗ excluded`}</ASCII>
            <CodeBlock code={`# Simple filter
high_cpu = df[df["cpu_pct"] > 70]

# AND — both conditions must be true (use &, not "and")
hot = df[(df["cpu_pct"] > 70) & (df["mem_pct"] > 80)]

# OR — either condition (use |, not "or")
needs_attn = df[(df["status"]=="warn") | (df["status"]=="crit")]

# isin() — match a list of values
mh_dl = df[df["region"].isin(["mh","dl"])]

# between() — inclusive range
mid_cpu = df[df["cpu_pct"].between(60, 85)]

print(hot[["cluster","cpu_pct","mem_pct"]])
print(needs_attn[["cluster","status","alerts"]])`} />
            <OutputBox>{`  cluster  cpu_pct  mem_pct
1   mh-02     85.1     88.5
3   dl-02     91.2     95.0

  cluster status  alerts
1   mh-02   warn       5
3   dl-02   crit       8`}</OutputBox>
          </div>
        )
      },
      {
        label:"query()",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>query()</code> lets you write filter conditions as readable strings.
              Great for complex conditions and method chaining.
            </Concept>
            <CodeBlock code={`# Simple query
print(df.query("cpu_pct > 70"))

# Multi-condition (use 'and'/'or' — not & |)
print(df.query("cpu_pct > 70 and mem_pct > 80"))

# Reference Python variable with @
threshold = 80
print(df.query("cpu_pct > @threshold"))

# String match
print(df.query("status in ['warn','crit']"))

# Not equal
print(df.query("region != 'tn'"))`} />
            <SQLCompare
              pandas={`df.query("cpu_pct > 70 and region == 'mh'")`}
              sql={`SELECT *\nFROM df\nWHERE cpu_pct > 70\n  AND region = 'mh'`}
            />
            <Exercises items={[
              "Find all clusters where cpu_pct > 80 AND alerts >= 5 — these need immediate attention",
              "Using isin(), find clusters from regions ['mh','tn'] only",
              "Use between() to find clusters with cpu_pct in the P25–P75 range (normal zone)",
              "Rewrite a 3-condition Boolean filter as a query() string and verify results are identical",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 4: DATA CLEANING ─────────────────────────────────────────────────
function S4() {
  return (
    <Tabs tabs={[
      {
        label:"Null Detection",
        content:(
          <div>
            <Concept>Null/NaN values represent missing data. They silently break calculations — mean(), sum(), and filters all behave unexpectedly with nulls present.</Concept>
            <ASCII>{`Null detection tools:
┌─────────────┬──────────────────────────────────┐
│ isnull()    │ True where value is NaN          │
│ notnull()   │ True where value is valid        │
│ isna()      │ Alias for isnull()               │
│ .sum()      │ Count nulls per column           │
│ .mean()*100 │ % of null values per column      │
└─────────────┴──────────────────────────────────┘`}</ASCII>
            <CodeBlock code={`import pandas as pd, numpy as np

df = pd.DataFrame({
    "pod":       ["nginx","redis",None,"kafka","nginx","redis"],
    "cpu_pct":   [72.3, np.nan, 45.6, 91.2, 72.3, 8.1],
    "memory_mb": [256, 128, np.nan, 512, 256, 128],
    "namespace": ["prod","prod","dev",None,"prod","prod"],
    "restarts":  [0, 2, np.nan, 1, 0, 2]
})

# Count nulls per column
print(df.isnull().sum())

# Percentage of nulls
print((df.isnull().mean() * 100).round(1))

# Rows with any null
print(df[df.isnull().any(axis=1)])

# Only rows with NO nulls
print(df[df.notnull().all(axis=1)])`} />
            <OutputBox>{`pod          1
cpu_pct      1
memory_mb    1
namespace    1
restarts     1
dtype: int64

pod          16.7%
cpu_pct      16.7%
memory_mb    16.7%
namespace    16.7%
restarts     16.7%`}</OutputBox>
          </div>
        )
      },
      {
        label:"fillna() & dropna()",
        content:(
          <div>
            <CodeBlock code={`# fillna — replace NaN with a value
df_filled = df.fillna({
    "cpu_pct":   df["cpu_pct"].mean(),  # fill numeric with mean
    "memory_mb": 0,                      # fill with 0
    "pod":       "unknown",              # fill string with placeholder
    "namespace": "default",
    "restarts":  0
})
print(df_filled)

# dropna — remove rows with nulls
df_clean = df.dropna()                         # any null in any column
print(f"Rows after dropna: {len(df_clean)}")

# Target specific columns (only care about these)
df_clean2 = df.dropna(subset=["pod","cpu_pct"])
print(f"Rows after targeted dropna: {len(df_clean2)}")

# dropna columns (drop any column that has a null)
df_clean3 = df.dropna(axis=1, how="any")
print(f"Cols remaining: {df_clean3.columns.tolist()}")`} />
            <PerfBox>
              Don't blindly drop nulls — you may remove significant data.
              Always check null % first. For time-series metric gaps, prefer{" "}
              <code style={{color:T.accent}}>ffill()</code> (forward-fill from last known value) or{" "}
              <code style={{color:T.accent}}>bfill()</code>. For categorical columns, fill with{" "}
              <code style={{color:T.accent}}>'unknown'</code> rather than dropping.
            </PerfBox>
          </div>
        )
      },
      {
        label:"Duplicates & Replace",
        content:(
          <div>
            <CodeBlock code={`# Detect duplicates
print("Total duplicates:", df.duplicated().sum())
print(df[df.duplicated(keep=False)])  # show ALL copies

# Remove duplicates
df_dedup = df.drop_duplicates()
print(f"After dedup: {len(df_dedup)} rows")

# Dedup by key columns (keep most recent = 'last')
df_dedup2 = df.drop_duplicates(subset=["pod"], keep="last")
print(df_dedup2[["pod","cpu_pct"]])

# replace() — remap values
df2 = df.copy()
df2["namespace"] = df2["namespace"].replace({
    "prod": "production",
    "dev":  "development"
})

# Replace NaN globally
df3 = df.copy().replace({np.nan: -1})`} />
            <InterviewQ items={[
              {q:"Difference between drop_duplicates keep='first' vs 'last'?", a:"keep='first' retains the first occurrence and drops later ones. keep='last' keeps the last. keep=False drops ALL rows that are duplicates — useful for inspecting duplicate sets."},
              {q:"Why use pd.to_numeric(errors='coerce') instead of astype(float)?", a:"astype(float) throws ValueError on any non-numeric value. errors='coerce' silently converts bad values to NaN, making it safe for real-world dirty data."},
            ]} />
          </div>
        )
      },
      {
        label:"Rename & dtypes",
        content:(
          <div>
            <CodeBlock code={`# Rename specific columns
df_renamed = df.rename(columns={
    "cpu_pct":   "cpu_utilization",
    "memory_mb": "memory_megabytes",
    "pod":       "pod_name"
})
print(df_renamed.columns.tolist())

# Rename all columns at once (must provide all names)
df.columns = ["pod","cpu","mem","ns","rst"]

# Change dtypes after loading
df2 = pd.DataFrame({
    "cpu": ["72.3", "85.1", "45.6"],  # loaded as strings!
    "alerts": ["2", "5", "0"]
})
print("Before:", df2.dtypes.tolist())

df2["cpu"]    = df2["cpu"].astype(float)
df2["alerts"] = df2["alerts"].astype(int)
print("After:", df2.dtypes.tolist())

# Safe conversion — bad values become NaN
df2["cpu"] = pd.to_numeric(df2["cpu"], errors="coerce")`} />
            <Exercises items={[
              "In a Prometheus metrics CSV, fill missing cpu_pct values with the per-namespace mean (hint: groupby + transform)",
              "Detect and remove duplicate pod entries keeping only the most recent (sort by timestamp first)",
              "A column 'restarts' was loaded as string. Convert it to int. Handle rows containing 'N/A'",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 5: TRANSFORMATION ────────────────────────────────────────────────
function S5() {
  return (
    <Tabs tabs={[
      {
        label:"Add & Update Cols",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd, numpy as np

df = pd.DataFrame({
    "pod":       ["nginx","redis","kafka","postgres"],
    "cpu_pct":   [72.3, 85.1, 45.6, 91.2],
    "mem_mb":    [256, 128, 512, 1024],
    "namespace": ["prod","prod","dev","prod"]
})

# Arithmetic column
df["mem_gb"] = df["mem_mb"] / 1024

# Conditional with np.where (fast — vectorized)
df["cpu_status"] = np.where(df["cpu_pct"] > 80, "high", "normal")

# Multi-condition with np.select
conditions = [
    df["cpu_pct"] < 50,
    df["cpu_pct"].between(50, 80),
    df["cpu_pct"] > 80
]
choices = ["low", "medium", "high"]
df["cpu_tier"] = np.select(conditions, choices, default="unknown")

# Binning with pd.cut
df["cpu_bin"] = pd.cut(df["cpu_pct"],
    bins=[0,50,75,100],
    labels=["low","mid","high"]
)
print(df[["pod","cpu_pct","cpu_tier","cpu_bin"]])`} />
            <OutputBox>{`        pod  cpu_pct cpu_tier cpu_bin
0     nginx     72.3   medium     mid
1     redis     85.1     high    high
2     kafka     45.6      low     low
3  postgres     91.2     high    high`}</OutputBox>
          </div>
        )
      },
      {
        label:"apply() & map()",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>map()</code> applies element-wise to a Series.{" "}
              <code style={{color:T.accent}}>apply()</code> applies a function to each row (axis=1) or column (axis=0).
              Both accept lambda functions or named functions.
            </Concept>
            <ASCII>{`map() flow (Series):              apply() flow (rows):
Series ─▶ f(element) ─▶ Series   DataFrame ─▶ f(row) ─▶ Series
  72.3 ─▶ "72%"                    row 0 ─▶ f({cpu:72.3,mem:256}) ─▶ score
  85.1 ─▶ "85%"                    row 1 ─▶ f({cpu:85.1,mem:128}) ─▶ score`}</ASCII>
            <CodeBlock code={`# map() on a Series — element-wise
df["cpu_label"] = df["cpu_pct"].map(lambda x: f"{x:.0f}%")

# map() with a dictionary — value replacement
ns_map = {"prod": "production", "dev": "development"}
df["ns_full"] = df["namespace"].map(ns_map)

# apply() on a column — min-max normalization
min_v, max_v = df["cpu_pct"].min(), df["cpu_pct"].max()
df["cpu_norm"] = df["cpu_pct"].apply(
    lambda x: (x - min_v) / (max_v - min_v)
)

# apply() on rows (axis=1) — composite scoring
def resource_score(row):
    return round((row["cpu_pct"] * 0.6) + (row["mem_mb"] / 1024 * 40), 2)

df["score"] = df.apply(resource_score, axis=1)
print(df[["pod","cpu_pct","cpu_norm","score"]].round(3))`} />
            <WarnBox items={[
              "❌ apply(axis=1) iterates row-by-row in Python — very slow on 1M+ rows",
              "✅ Prefer np.where / np.select / vectorized arithmetic for simple logic",
              "✅ Use apply(axis=1) only when the logic is truly too complex for vectorization",
            ]} />
          </div>
        )
      },
      {
        label:"String Operations",
        content:(
          <div>
            <Concept>
              The <code style={{color:T.accent}}>.str</code> accessor gives vectorized string methods
              — no loops. Essential for parsing log messages, pod names, and Kubernetes labels.
            </Concept>
            <CodeBlock code={`df = pd.DataFrame({"log": [
    "ERROR 2024-01-15 pod/nginx-abc123 OOMKilled",
    "WARN  2024-01-15 pod/redis-def456 HighCPU",
    "INFO  2024-01-15 pod/kafka-ghi789 Running",
    "ERROR 2024-01-15 pod/postgres-jkl012 CrashLoopBackOff"
]})

df["level"]    = df["log"].str.split().str[0].str.strip()
df["date"]     = df["log"].str.split().str[1]
df["pod"]      = df["log"].str.extract(r"pod/(\\S+)")
df["pod_base"] = df["pod"].str.rsplit("-", n=1).str[0]
df["reason"]   = df["log"].str.split().str[-1]
df["is_error"] = df["level"].str.startswith("ERROR")
df["is_fatal"] = df["reason"].str.contains("OOMKilled|CrashLoop")

print(df[["level","pod_base","reason","is_error","is_fatal"]])`} />
            <OutputBox>{`  level   pod_base           reason  is_error  is_fatal
0  ERROR      nginx        OOMKilled      True      True
1   WARN      redis          HighCPU     False     False
2   INFO      kafka          Running     False     False
3  ERROR   postgres  CrashLoopBackOff      True      True`}</OutputBox>
          </div>
        )
      },
      {
        label:"DateTime Operations",
        content:(
          <div>
            <CodeBlock code={`df = pd.DataFrame({
    "timestamp": pd.to_datetime([
        "2024-01-15 08:30:00","2024-01-15 14:22:00",
        "2024-01-16 02:15:00","2024-01-16 18:45:00"
    ]),
    "cpu_pct": [72.3, 85.1, 45.6, 91.2],
    "cluster": ["mh-01","mh-01","dl-01","dl-01"]
})

# Extract components
df["hour"]    = df["timestamp"].dt.hour
df["weekday"] = df["timestamp"].dt.day_name()
df["date"]    = df["timestamp"].dt.date

# Is business hours? (9am–6pm)
df["is_biz"]  = df["hour"].between(9, 18)

# Unix epoch (Prometheus format) → datetime
df_prom = pd.DataFrame({"epoch": [1705296000, 1705299600], "cpu": [72, 85]})
df_prom["ts"] = pd.to_datetime(df_prom["epoch"], unit="s")

# Time difference
df["age_hrs"] = (pd.Timestamp.now() - df["timestamp"]).dt.total_seconds() / 3600

# Date range filter
recent = df[df["timestamp"] >= "2024-01-16"]
print(f"Recent rows: {len(recent)}")
print(df[["timestamp","hour","weekday","is_biz"]])`} />
            <Exercises items={[
              "Parse Kubernetes event log strings: extract namespace, pod name, event type, and message",
              "Create a cpu_category column using pd.cut() with bins [0,30,70,100] and labels ['low','medium','high']",
              "Given alert timestamps, identify which fired during business hours vs nights/weekends",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 6: SORTING & RANKING ─────────────────────────────────────────────
function S6() {
  return (
    <Tabs tabs={[
      {
        label:"sort_values()",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd

df = pd.DataFrame({
    "cluster": ["mh-01","mh-02","dl-01","dl-02","tn-01","tn-02"],
    "cpu_pct": [72.3, 85.1, 45.6, 91.2, 60.0, 91.2],
    "alerts":  [2, 5, 0, 8, 1, 8],
    "region":  ["mh","mh","dl","dl","tn","tn"]
})

# Sort ascending (default) — coolest clusters first
print(df.sort_values("cpu_pct").head(3))

# Sort descending — hottest clusters first
print(df.sort_values("cpu_pct", ascending=False).head(3))

# Multi-column sort: region ASC, then cpu DESC
print(df.sort_values(["region","cpu_pct"], ascending=[True,False]))

# Sort and reset index cleanly
df_sorted = (df.sort_values("cpu_pct", ascending=False)
               .reset_index(drop=True))
print(df_sorted)`} />
            <SQLCompare
              pandas={`df.sort_values(["region","cpu_pct"],\n  ascending=[True, False])`}
              sql={`SELECT *\nFROM df\nORDER BY region ASC,\n         cpu_pct DESC`}
            />
          </div>
        )
      },
      {
        label:"nlargest & nsmallest",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>nlargest(n)</code> and{" "}
              <code style={{color:T.accent}}>nsmallest(n)</code> are optimized shortcuts to get top/bottom N rows.
              Faster than sort_values().head(n) on large DataFrames.
            </Concept>
            <CodeBlock code={`# Top 3 hottest clusters
print(df.nlargest(3, "cpu_pct"))

# Coolest 3 clusters
print(df.nsmallest(3, "cpu_pct"))

# nlargest on a Series
print(df["cpu_pct"].nlargest(3))

# Keep ties with keep='all'
print(df.nlargest(3, "cpu_pct", keep="all"))

# Top 5 by composite: most alerts + highest cpu
df["risk_score"] = df["cpu_pct"] * 0.7 + df["alerts"] * 3
print(df.nlargest(3, "risk_score")[["cluster","cpu_pct","alerts","risk_score"]])`} />
            <OutputBox>{`  cluster  cpu_pct  alerts region
3   dl-02     91.2       8     dl
5   tn-02     91.2       8     tn
1   mh-02     85.1       5     mh

  cluster  cpu_pct  alerts  risk_score
3   dl-02     91.2       8       87.84
5   tn-02     91.2       8       87.84
1   mh-02     85.1       5       74.57`}</OutputBox>
          </div>
        )
      },
      {
        label:"rank()",
        content:(
          <div>
            <CodeBlock code={`# Rank by cpu (1 = lowest)
df["cpu_rank"] = df["cpu_pct"].rank()

# Rank highest first (1 = most critical)
df["alert_rank"] = df["alerts"].rank(ascending=False)

# Dense rank (ties get same rank, no gaps)
df["dense"] = df["cpu_pct"].rank(method="dense", ascending=False)

print(df[["cluster","cpu_pct","cpu_rank","dense"]])
# rank methods:
# "average" (default) — ties get avg of their ranks
# "min"  — ties get the lowest rank
# "max"  — ties get the highest rank
# "first"— ties ranked by order of appearance
# "dense"— ties share rank, next rank is consecutive (like SQL DENSE_RANK)`} />
            <Exercises items={[
              "Find the top 5 pods with highest memory usage across all namespaces",
              "Rank clusters by composite score: cpu_pct*0.6 + (alerts*10)*0.4. Show descending",
              "Sort by region, then by alerts descending, to produce a prioritized alert report",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 7: AGGREGATION ────────────────────────────────────────────────────
function S7() {
  return (
    <Tabs tabs={[
      {
        label:"Numeric Aggregations",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd

df = pd.DataFrame({
    "cluster":  ["mh-01","mh-02","dl-01","dl-02","tn-01"],
    "cpu_pct":  [72.3, 85.1, 45.6, 91.2, 60.0],
    "alerts":   [2, 5, 0, 8, 1],
    "status":   ["ok","warn","ok","crit","ok"],
    "region":   ["mh","mh","dl","dl","tn"]
})

print("sum:   ", df["cpu_pct"].sum())
print("mean:  ", df["cpu_pct"].mean())
print("median:", df["cpu_pct"].median())
print("min:   ", df["cpu_pct"].min())
print("max:   ", df["cpu_pct"].max())
print("std:   ", df["cpu_pct"].std().round(2))

# Multiple aggregations at once
stats = df["cpu_pct"].agg(["mean","median","std","min","max"])
print(stats)

# Across multiple columns
print(df[["cpu_pct","alerts"]].agg(["mean","sum","max"]))`} />
            <OutputBox>{`sum:    354.2
mean:    70.84
median:  72.3
min:     45.6
max:     91.2
std:     18.19

mean      70.840
median    72.300
std       18.190
min       45.600
max       91.200

       cpu_pct  alerts
mean     70.84     3.2
sum     354.20    16.0
max      91.20     8.0`}</OutputBox>
          </div>
        )
      },
      {
        label:"value_counts()",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>value_counts()</code> counts occurrences of each unique value.
              Essential for understanding categorical distributions — status breakdowns, region splits, etc.
            </Concept>
            <CodeBlock code={`# Count each status
print(df["status"].value_counts())

# Normalize → proportions
print(df["status"].value_counts(normalize=True).round(3))

# Bin numeric data and count distribution
bins = pd.cut(df["cpu_pct"], bins=[0,50,75,100], labels=["low","mid","high"])
print(bins.value_counts().sort_index())

# Sort alphabetically instead of by count
print(df["region"].value_counts().sort_index())`} />
            <SQLCompare
              pandas={`df["status"].value_counts()`}
              sql={`SELECT status, COUNT(*) AS count\nFROM df\nGROUP BY status\nORDER BY count DESC`}
            />
          </div>
        )
      },
      {
        label:"unique() & nunique()",
        content:(
          <div>
            <CodeBlock code={`# unique() → array of unique values
print(df["status"].unique())    # ['ok' 'warn' 'crit']
print(df["region"].unique())    # ['mh' 'dl' 'tn']

# nunique() → count of unique values
print(df["status"].nunique())   # 3
print(df["region"].nunique())   # 3

# Per-column cardinality report
for col in df.columns:
    n = df[col].nunique()
    pct = n / len(df) * 100
    dtype = df[col].dtype
    print(f"{col:12s}: {n:3d} unique ({pct:.0f}%) [{dtype}]")`} />
            <OutputBox>{`['ok' 'warn' 'crit']
['mh' 'dl' 'tn']
3
3

cluster    :   5 unique (100%) [object]
cpu_pct    :   5 unique (100%) [float64]
alerts     :   5 unique (100%) [int64]
status     :   3 unique (60%)  [object]
region     :   3 unique (60%)  [object]`}</OutputBox>
            <PerfBox>
              Low-cardinality columns (like status, region) with few unique values repeated many times
              should be converted to <code style={{color:T.accent}}>category</code> dtype.
              This can reduce memory by 50–90% on large datasets. See Section 13 for details.
            </PerfBox>
            <Exercises items={[
              "Compute P90, P95, P99 of cpu_pct using quantile() — these are your SLO thresholds",
              "What percentage of clusters are in 'crit' state? Use value_counts(normalize=True)",
              "Use value_counts(bins=5) to bucket cpu_pct into 5 equal-width ranges",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 8: GROUPBY DEEP DIVE ──────────────────────────────────────────────
function S8() {
  return (
    <Tabs tabs={[
      {
        label:"groupby() Basics",
        content:(
          <div>
            <Concept>GroupBy implements the split-apply-combine pattern: split data into groups, apply a function to each group independently, combine results into a single output.</Concept>
            <ASCII>{`Original:                  After groupby("region").mean():
  region  cpu  alerts      region │ cpu   │ alerts
  mh      72   2           ───────┼───────┼───────
  mh      85   5           mh     │ 78.7  │  3.5
  dl      46   0           dl     │ 68.4  │  4.0
  dl      91   8           tn     │ 60.0  │  1.0
  tn      60   1`}</ASCII>
            <CodeBlock code={`import pandas as pd

df = pd.DataFrame({
    "cluster": ["mh-01","mh-02","dl-01","dl-02","tn-01"],
    "cpu_pct": [72.3, 85.1, 45.6, 91.2, 60.0],
    "mem_pct": [65.0, 88.5, 50.2, 95.0, 71.3],
    "alerts":  [2, 5, 0, 8, 1],
    "region":  ["mh","mh","dl","dl","tn"]
})

# Group by region → mean cpu
print(df.groupby("region")["cpu_pct"].mean())

# Multi-column aggregation
print(df.groupby("region").agg({
    "cpu_pct": "mean",
    "alerts":  "sum",
    "cluster": "count"
}))`} />
            <SQLCompare
              pandas={`df.groupby("region").agg({\n  "cpu_pct": "mean",\n  "alerts": "sum"\n})`}
              sql={`SELECT region,\n  AVG(cpu_pct),\n  SUM(alerts)\nFROM df\nGROUP BY region`}
            />
          </div>
        )
      },
      {
        label:"Named Aggregations",
        content:(
          <div>
            <Concept>Named aggregations (Pandas 0.25+) let you name output columns directly — cleaner than flattening MultiIndex. Use the syntax: <code style={{color:T.accent}}>output_name=('column', 'agg_func')</code></Concept>
            <CodeBlock code={`result = df.groupby("region").agg(
    avg_cpu      = ("cpu_pct",  "mean"),
    max_cpu      = ("cpu_pct",  "max"),
    min_cpu      = ("cpu_pct",  "min"),
    total_alerts = ("alerts",   "sum"),
    cluster_cnt  = ("cluster",  "count"),
    p90_cpu      = ("cpu_pct",  lambda x: x.quantile(0.90))
)
print(result.round(2))`} />
            <OutputBox>{`         avg_cpu  max_cpu  min_cpu  total_alerts  cluster_cnt  p90_cpu
region
dl         68.40     91.2     45.6             8            2    85.20
mh         78.70     85.1     72.3             7            2    83.86
tn         60.00     60.0     60.0             1            1    60.00`}</OutputBox>
          </div>
        )
      },
      {
        label:"Transform & Filter",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>transform()</code> returns a result with the SAME shape as the original
              DataFrame — useful for adding group statistics back as new columns.{" "}
              <code style={{color:T.accent}}>filter()</code> removes entire groups based on a condition.
            </Concept>
            <CodeBlock code={`# transform — add group mean back to each row
df["region_avg_cpu"] = df.groupby("region")["cpu_pct"].transform("mean")
df["cpu_deviation"]  = df["cpu_pct"] - df["region_avg_cpu"]
print(df[["cluster","region","cpu_pct","region_avg_cpu","cpu_deviation"]].round(2))

# transform with custom function
df["pct_of_region"] = df.groupby("region")["cpu_pct"].transform(
    lambda x: x / x.sum() * 100
)

# filter — keep only groups where max cpu > 80
hot_regions = df.groupby("region").filter(lambda g: g["cpu_pct"].max() > 80)
print(hot_regions[["cluster","region","cpu_pct"]])`} />
            <OutputBox>{`  cluster region  cpu_pct  region_avg_cpu  cpu_deviation
0   mh-01     mh     72.3           78.70          -6.40
1   mh-02     mh     85.1           78.70           6.40
2   dl-01     dl     45.6           68.40         -22.80
3   dl-02     dl     91.2           68.40          22.80
4   tn-01     tn     60.0           60.00           0.00`}</OutputBox>
            <Exercises items={[
              "Group Prometheus metrics by namespace: compute mean, P95, max cpu_pct, and total alert count",
              "Use transform() to add 'pct_of_region_cpu' showing each cluster's CPU as % of its region total",
              "Filter to keep only regions where at least one cluster is in 'crit' status",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 9: COMBINING DATAFRAMES ───────────────────────────────────────────
function S9() {
  return (
    <Tabs tabs={[
      {
        label:"concat()",
        content:(
          <div>
            <ASCII>{`Vertical concat (stack rows):      Horizontal concat (stack cols):
┌───────┐                           ┌──────┬──────┐
│  mh   │  rows 0-1                 │ names│ stats│
├───────┤                           │──────┼──────│
│  dl   │  rows 2-3      axis=1 →   │mh-01 │ 72.3 │
├───────┤                           │mh-02 │ 85.1 │
│  tn   │  row  4                   └──────┴──────┘
└───────┘`}</ASCII>
            <CodeBlock code={`import pandas as pd

mh = pd.DataFrame({"cluster":["mh-01","mh-02"],"cpu":[72.3,85.1],"region":["mh","mh"]})
dl = pd.DataFrame({"cluster":["dl-01","dl-02"],"cpu":[45.6,91.2],"region":["dl","dl"]})
tn = pd.DataFrame({"cluster":["tn-01"],"cpu":[60.0],"region":["tn"]})

# Stack rows
all_clusters = pd.concat([mh, dl, tn], ignore_index=True)
print(all_clusters)

# With source keys (creates MultiIndex)
all_keyed = pd.concat([mh, dl, tn], keys=["maharashtra","delhi","tamilnadu"])
print(all_keyed.loc["maharashtra"])

# Horizontal — add columns
names = pd.DataFrame({"cluster":["mh-01","mh-02"]})
stats = pd.DataFrame({"cpu":[72.3,85.1],"mem":[256,128]})
combined = pd.concat([names, stats], axis=1)
print(combined)`} />
          </div>
        )
      },
      {
        label:"merge() Joins",
        content:(
          <div>
            <ASCII>{`JOIN types:
INNER:  only rows matching in BOTH DataFrames
LEFT:   all left rows + matching right  (NaN if no match)
RIGHT:  all right rows + matching left
OUTER:  all rows from both (NaN where no match)`}</ASCII>
            <CodeBlock code={`clusters = pd.DataFrame({
    "cluster": ["mh-01","mh-02","dl-01","tn-01"],
    "region":  ["mh","mh","dl","tn"],
    "nodes":   [3, 5, 4, 3]
})
metrics = pd.DataFrame({
    "cluster": ["mh-01","mh-02","dl-01","dl-02"],  # dl-02 not in clusters!
    "cpu_pct": [72.3, 85.1, 45.6, 91.2]
})

# INNER — only matched clusters
inner = pd.merge(clusters, metrics, on="cluster", how="inner")
print("INNER:", len(inner), "rows", inner["cluster"].tolist())

# LEFT — all clusters, even those without metrics
left = pd.merge(clusters, metrics, on="cluster", how="left")
print("LEFT:", len(left), "rows")
print(left)   # tn-01 has NaN for cpu_pct

# OUTER — everything from both
outer = pd.merge(clusters, metrics, on="cluster", how="outer")
print("OUTER:", len(outer), "rows")`} />
            <SQLCompare
              pandas={`pd.merge(clusters, metrics,\n  on="cluster", how="left")`}
              sql={`SELECT *\nFROM clusters c\nLEFT JOIN metrics m\n  ON c.cluster = m.cluster`}
            />
          </div>
        )
      },
      {
        label:"merge() Tips",
        content:(
          <div>
            <CodeBlock code={`# Different column names
df_a = pd.DataFrame({"pod_id":["p1","p2"],"cpu":[72,85]})
df_b = pd.DataFrame({"id":["p1","p2"],"namespace":["prod","dev"]})
merged = pd.merge(df_a, df_b, left_on="pod_id", right_on="id")
print(merged)

# Overlapping column names → suffixes
df_c = pd.DataFrame({"id":["p1"],"value":[100]})
df_d = pd.DataFrame({"id":["p1"],"value":[200]})
m2 = pd.merge(df_c, df_d, on="id", suffixes=("_old","_new"))
print(m2)

# join() — shortcut for index-based merge
c2 = clusters.set_index("cluster")
m2 = metrics.set_index("cluster")
result = c2.join(m2, how="left")
print(result)`} />
            <InterviewQ items={[
              {q:"What is the difference between merge() and join()?", a:"merge() is more flexible — it can join on any column(s) or index. join() is a shortcut that joins on the index by default. Under the hood, join() calls merge() with left_index=True or right_index=True."},
              {q:"What happens when merge creates duplicate column names?", a:"Pandas adds _x and _y suffixes automatically. Control this with the suffixes=('_left','_right') parameter."},
            ]} />
            <Exercises items={[
              "Merge cluster inventory with Prometheus metrics. Find clusters with no metrics (LEFT join + filter NaN)",
              "Concatenate DataFrames from 3 regions. Add a 'source_region' column before concatenating",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 10: PIVOT & RESHAPE ───────────────────────────────────────────────
function S10() {
  return (
    <Tabs tabs={[
      {
        label:"pivot_table()",
        content:(
          <div>
            <ASCII>{`Long format → Wide (pivot):
cluster  metric    value        metric  │  cpu   │ memory
mh-01    cpu       72.3   →  cluster   │        │
mh-01    memory    256         mh-01   │  72.3  │  256
mh-02    cpu       85.1        mh-02   │  85.1  │  128`}</ASCII>
            <CodeBlock code={`import pandas as pd

df_long = pd.DataFrame({
    "cluster": ["mh-01","mh-01","mh-02","mh-02","dl-01","dl-01"],
    "metric":  ["cpu","memory","cpu","memory","cpu","memory"],
    "value":   [72.3, 256, 85.1, 128, 45.6, 512],
    "region":  ["mh","mh","mh","mh","dl","dl"]
})

# pivot() — exact mapping, no aggregation needed
df_wide = df_long.pivot(index="cluster", columns="metric", values="value")
print(df_wide)

# pivot_table() — handles duplicates with aggregation
df_pt = df_long.pivot_table(
    index="cluster",
    columns="metric",
    values="value",
    aggfunc="mean"      # or "sum", "max", etc.
)
print(df_pt)

# Add margins (totals row/col)
df_totals = df_long.pivot_table(
    index="region", columns="metric", values="value",
    aggfunc="mean", margins=True, margins_name="ALL"
)
print(df_totals.round(1))`} />
          </div>
        )
      },
      {
        label:"melt()",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>melt()</code> is the reverse of pivot — converts wide format to long.
              Essential for feeding data into visualization libraries and ML pipelines that expect tidy data.
            </Concept>
            <CodeBlock code={`df_wide = pd.DataFrame({
    "cluster": ["mh-01","mh-02","dl-01"],
    "cpu":     [72.3, 85.1, 45.6],
    "memory":  [256, 128, 512],
    "alerts":  [2, 5, 0]
})

# melt — wide to long
df_melted = df_wide.melt(
    id_vars=["cluster"],                    # keep as identifier
    value_vars=["cpu","memory","alerts"],   # pivot these into rows
    var_name="metric",                      # column name for variable
    value_name="value"                      # column name for value
)
print(df_melted.sort_values("cluster"))`} />
            <OutputBox>{`   cluster  metric  value
0   mh-01     cpu   72.3
3   mh-01  memory  256.0
6   mh-01  alerts    2.0
1   mh-02     cpu   85.1
4   mh-02  memory  128.0
7   mh-02  alerts    5.0
2   dl-01     cpu   45.6
5   dl-01  memory  512.0
8   dl-01  alerts    0.0`}</OutputBox>
            <UseCaseGrid items={[
              {label:"Prometheus", text:"Prometheus exports are already in long format. melt() helps convert wide reports back to metric format."},
              {label:"ML Features", text:"Scikit-learn expects wide format. pivot() converts long metric logs into feature matrices per entity."},
            ]} />
          </div>
        )
      },
      {
        label:"stack() & unstack()",
        content:(
          <div>
            <CodeBlock code={`df = pd.DataFrame({
    "cpu": [72.3, 85.1],
    "mem": [256, 128]
}, index=["mh-01", "mh-02"])

print(df)

# stack() — columns become index levels (wide → long)
stacked = df.stack()
print("\\nStacked:")
print(stacked)

# unstack() — index level becomes columns (long → wide)
unstacked = stacked.unstack()
print("\\nUnstacked:")
print(unstacked)`} />
            <OutputBox>{`        cpu  mem
mh-01  72.3  256
mh-02  85.1  128

Stacked:
mh-01  cpu     72.3
       mem    256.0
mh-02  cpu     85.1
       mem    128.0
dtype: float64

Unstacked:
        cpu    mem
mh-01  72.3  256.0
mh-02  85.1  128.0`}</OutputBox>
            <Exercises items={[
              "Given Prometheus long-format data (timestamp, metric_name, value), pivot to wide with one column per metric",
              "Create a cluster × region heatmap DataFrame using pivot_table() with cpu_pct as values",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 11: WORKING WITH FILES ────────────────────────────────────────────
function S11() {
  return (
    <Tabs tabs={[
      {
        label:"CSV & Excel",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd

# Read CSV with all common options
df = pd.read_csv("metrics.csv",
    sep=",",                         # delimiter (default)
    header=0,                        # first row = column names
    usecols=["cluster","cpu_pct"],   # load only needed columns
    dtype={"alerts": int},           # force dtype at load time
    parse_dates=["timestamp"],       # auto-parse date columns
    nrows=10000,                     # read first 10K rows only
    na_values=["N/A","null","-"]     # what to treat as NaN
)

# Write CSV
df.to_csv("output.csv", index=False)    # index=False → skip row numbers
df.to_csv("output.tsv", sep="\\t")        # TSV format

# Read Excel
df_xl = pd.read_excel("clusters.xlsx",
    sheet_name="Sheet1",
    usecols="A:D"                    # read columns A through D
)

# Write multiple sheets
with pd.ExcelWriter("report.xlsx", engine="openpyxl") as writer:
    df.to_excel(writer, sheet_name="Metrics", index=False)
    df2.to_excel(writer, sheet_name="Summary", index=False)
print("Written: 2-sheet Excel report")`} />
          </div>
        )
      },
      {
        label:"JSON & Parquet",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd, io

# Flat JSON array
json_str = '[{"pod":"nginx","cpu":72.3},{"pod":"redis","cpu":8.1}]'
df = pd.read_json(io.StringIO(json_str))

# Nested JSON (Kubernetes API response style)
import json
nested = {
    "clusters": [
        {"name":"mh-01","metrics":{"cpu":72.3,"mem":256}},
        {"name":"mh-02","metrics":{"cpu":85.1,"mem":128}}
    ]
}
df_nested = pd.json_normalize(nested["clusters"], sep="_")
print(df_nested)
# Output: name  metrics_cpu  metrics_mem

# Write JSON
df.to_json("out.json", orient="records", indent=2)

# Parquet — columnar, compressed, schema-preserving
# pip install pyarrow
df.to_parquet("metrics.parquet", compression="snappy", index=False)

# Read Parquet — only load needed columns (columnar advantage!)
df_pq = pd.read_parquet("metrics.parquet", columns=["pod","cpu"])
print(df_pq)`} />
            <ASCII>{`Format comparison:
Format   │ Relative Size │ Read Speed │ Schema │ Best for
─────────┼───────────────┼────────────┼────────┼──────────────────────
CSV      │      1x       │   slow     │   no   │ sharing, debugging
JSON     │      1.3x     │   slow     │   no   │ APIs, nested data
Parquet  │      0.2x     │   fast     │   yes  │ analytics, pipelines
Excel    │      1.5x     │   slow     │   no   │ business reporting`}</ASCII>
          </div>
        )
      },
      {
        label:"Large Files",
        content:(
          <div>
            <Concept>For files larger than available RAM, use chunked reading, memory dtype optimization, or Parquet column selection.</Concept>
            <CodeBlock code={`import pandas as pd

# 1. Chunked reading — process in pieces
results = []
for chunk in pd.read_csv("huge_metrics.csv", chunksize=100_000):
    filtered = chunk[chunk["cpu_pct"] > 80]
    agg = filtered.groupby("cluster")["cpu_pct"].mean()
    results.append(agg)
final = pd.concat(results).groupby(level=0).mean()

# 2. usecols — load only what you need
df = pd.read_csv("huge.csv", usecols=["timestamp","cpu_pct","cluster"])

# 3. dtype map — massive memory reduction
dtype_map = {
    "cluster":   "category",    # string → category
    "cpu_pct":   "float32",     # 8 bytes → 4 bytes per value
    "alerts":    "int16",       # 8 bytes → 2 bytes
    "status":    "category"
}
df = pd.read_csv("metrics.csv", dtype=dtype_map)
mb_after = df.memory_usage(deep=True).sum() / 1024**2
print(f"Memory after optimization: {mb_after:.1f} MB")

# 4. For truly huge data (100GB+): Dask
# import dask.dataframe as dd
# df = dd.read_csv("huge.csv")
# result = df[df.cpu_pct > 80].groupby("cluster").mean().compute()`} />
            <Exercises items={[
              "Read a 1M-row Prometheus CSV in chunks. Keep only crit alerts per chunk. Concatenate and compute final stats",
              "Compare memory: float64 vs float32 vs category dtypes on a 500K-row sample. Measure each",
              "Save a DataFrame as Parquet. Read it back selecting only 2 columns. Time vs CSV read",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 12: TIME SERIES ────────────────────────────────────────────────────
function S12() {
  return (
    <Tabs tabs={[
      {
        label:"Datetime Conversion",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd, numpy as np

# Create time series (Prometheus scrape-style)
idx = pd.date_range("2024-01-15 00:00", periods=24, freq="1h")
df = pd.DataFrame({
    "cpu_pct": np.random.uniform(40, 95, 24).round(1),
    "mem_pct": np.random.uniform(50, 90, 24).round(1)
}, index=idx)
print(df.head(3))

# Parse string timestamps
ts_df = pd.DataFrame({
    "timestamp": ["2024-01-15 08:30:00","2024-01-15 09:45:00"],
    "cpu": [72.3, 85.1]
})
ts_df["timestamp"] = pd.to_datetime(ts_df["timestamp"])
ts_df = ts_df.set_index("timestamp")

# Unix epoch (Prometheus format) → datetime
df_prom = pd.DataFrame({
    "ts_epoch": [1705296000, 1705299600],
    "cpu": [72, 85]
})
df_prom["timestamp"] = pd.to_datetime(df_prom["ts_epoch"], unit="s")
print(df_prom)`} />
          </div>
        )
      },
      {
        label:"Resampling",
        content:(
          <div>
            <Concept>
              <code style={{color:T.accent}}>resample()</code> groups time series into time buckets
              — like groupby() for time. Used to downsample 1-min Prometheus data to 1-hour averages.
            </Concept>
            <CodeBlock code={`# Resample hourly data to 6-hour averages
hourly_6h = df.resample("6h").mean()
print(hourly_6h)

# Multiple aggregations
daily = df.resample("1D").agg({
    "cpu_pct": ["mean","max","min"],
    "mem_pct": "mean"
})
print(daily)

# Common frequency aliases:
# "1min"/"1T"   "5min"/"5T"   "1h"/"1H"
# "1D"=daily    "1W"=weekly   "1ME"=month-end

# Fill gaps (missed scrapes)
df_30min = df.resample("30min").mean()
df_filled = df_30min.ffill()    # forward-fill NaN gaps
print(df_filled.head(6))`} />
          </div>
        )
      },
      {
        label:"Rolling Windows",
        content:(
          <div>
            <ASCII>{`Rolling window (size=3):
Time:  t1   t2   t3   t4   t5
CPU:   70   80   60   90   50
               └───┘ window at t3 = mean(70,80,60) = 70.0
                    └───┘ window at t4 = mean(80,60,90) = 76.7
                         └───┘ window at t5 = mean(60,90,50) = 66.7`}</ASCII>
            <CodeBlock code={`# Rolling average (smooths noise)
df["cpu_3h_avg"] = df["cpu_pct"].rolling(window=3).mean()

# Rolling max (detect sustained spikes)
df["cpu_6h_max"] = df["cpu_pct"].rolling(window=6).max()

# Exponential weighted (recent data weighted more heavily)
df["cpu_ewma"] = df["cpu_pct"].ewm(span=3).mean()

# Bollinger bands for anomaly detection
df["roll_mean"] = df["cpu_pct"].rolling(6).mean()
df["roll_std"]  = df["cpu_pct"].rolling(6).std()
df["upper_band"] = df["roll_mean"] + 2 * df["roll_std"]
df["lower_band"] = df["roll_mean"] - 2 * df["roll_std"]
df["is_spike"]   = df["cpu_pct"] > df["upper_band"]

spikes = df[df["is_spike"]]
print(f"Anomaly events detected: {len(spikes)}")
print(df[["cpu_pct","cpu_3h_avg","upper_band","is_spike"]].head(8).round(1))`} />
            <UseCaseGrid items={[
              {label:"SLO Analysis", text:"Resample to 5-min windows, compute error rate, identify windows exceeding SLO threshold"},
              {label:"Anomaly Detection", text:"Rolling mean ± 2×std gives dynamic thresholds — better than static Prometheus alert values"},
            ]} />
          </div>
        )
      },
      {
        label:"Time-based Filtering",
        content:(
          <div>
            <CodeBlock code={`# Filter by date range string
morning = df["2024-01-15 06:00":"2024-01-15 12:00"]
print(f"Morning: {len(morning)} rows")

# Filter by hour of day
biz_hours = df[df.index.hour.isin(range(9, 18))]
nights = df[~df.index.hour.isin(range(9, 18))]

# Lag/lead features for anomaly detection
df["cpu_1h_ago"] = df["cpu_pct"].shift(1)      # lag by 1 period
df["cpu_delta"]  = df["cpu_pct"] - df["cpu_pct"].shift(1)

# Detect sudden spikes: > 20% jump
spikes = df[df["cpu_delta"].abs() > 20]
print(f"Sudden spikes: {len(spikes)}")

# Time since last event
df["mins"] = (df.index - df.index[0]).total_seconds() / 60`} />
            <Exercises items={[
              "Load Prometheus TSDB data (epoch timestamps). Convert, resample to 5-min, compute 15-min rolling avg",
              "Identify windows where cpu_pct > rolling_mean + 2×rolling_std (statistical anomalies)",
              "Split a day's metrics into business-hours and after-hours DataFrames. Compare average cpu",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 13: PERFORMANCE ────────────────────────────────────────────────────
function S13() {
  return (
    <Tabs tabs={[
      {
        label:"Vectorization",
        content:(
          <div>
            <Concept>Vectorized operations run in optimized C code on entire arrays. Python loops run one element at a time in slow interpreted bytecode. The performance gap is typically 10x–1000x.</Concept>
            <ASCII>{`Python loop (slow):          Vectorized (fast):
for i in range(len(df)):     df["result"] = df["cpu"] * 1.1
    df["result"][i] =
      df["cpu"][i] * 1.1
  ↑ Python overhead            ↑ NumPy C extension
    for every single row         processes all rows at once`}</ASCII>
            <CodeBlock code={`import pandas as pd, numpy as np, time

n = 1_000_000
df = pd.DataFrame({"cpu": np.random.uniform(10, 95, n)})

# Method 1: Python loop (NEVER do this)
t0 = time.time()
result = []
for val in df["cpu"]:
    result.append(val * 1.1 if val > 80 else val)
df["m1"] = result
print(f"Loop:        {time.time()-t0:.3f}s")

# Method 2: apply() (better, still slow)
t0 = time.time()
df["m2"] = df["cpu"].apply(lambda x: x*1.1 if x > 80 else x)
print(f"apply():     {time.time()-t0:.3f}s")

# Method 3: np.where (vectorized — fast)
t0 = time.time()
df["m3"] = np.where(df["cpu"] > 80, df["cpu"] * 1.1, df["cpu"])
print(f"np.where:    {time.time()-t0:.3f}s")

# Method 4: direct arithmetic (fastest for simple ops)
t0 = time.time()
df["m4"] = df["cpu"] * 1.1
print(f"vectorized:  {time.time()-t0:.3f}s")`} />
            <OutputBox>{`Loop:        3.241s
apply():     0.892s
np.where:    0.012s
vectorized:  0.003s

→ np.where is 270x faster than a Python loop!
→ Always vectorize. Only use apply() when logic is truly complex.`}</OutputBox>
          </div>
        )
      },
      {
        label:"Memory Optimization",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd, numpy as np

n = 1_000_000
df = pd.DataFrame({
    "cluster": np.random.choice(["mh-01","mh-02","dl-01","tn-01"], n),
    "cpu_pct": np.random.uniform(10, 95, n).astype("float64"),
    "alerts":  np.random.randint(0, 20, n).astype("int64"),
    "status":  np.random.choice(["ok","warn","crit"], n)
})

def mem_mb(df, label):
    mb = df.memory_usage(deep=True).sum() / 1024**2
    print(f"{label}: {mb:.1f} MB")

mem_mb(df, "Before")

# float64 → float32 (8 bytes → 4 bytes, 50% reduction)
df["cpu_pct"] = df["cpu_pct"].astype("float32")

# int64 → int8 (alerts max=20 fits in int8 range -128:127)
df["alerts"] = df["alerts"].astype("int8")

# object string → category (90% reduction for low cardinality)
df["cluster"] = df["cluster"].astype("category")
df["status"]  = df["status"].astype("category")

mem_mb(df, "After")`} />
            <OutputBox>{`Before: 142.7 MB
After:   18.3 MB

→ 87% memory reduction on the same data!
→ Operations on category columns are also faster in groupby()`}</OutputBox>
          </div>
        )
      },
      {
        label:"Best Practices",
        content:(
          <div>
            <InfoBox title="Top 6 Performance Rules" color={T.green} bgColor="#0C1A0E" borderColor="#14532D">
              <div style={{lineHeight:2}}>
                <div>1. <strong style={{color:T.textPri}}>Never loop</strong> — always prefer vectorized ops, np.where, np.select</div>
                <div>2. <strong style={{color:T.textPri}}>Downcast numerics</strong> — float32/int16/int8 where precision allows</div>
                <div>3. <strong style={{color:T.textPri}}>Use category</strong> — for low-cardinality strings (status, region, namespace)</div>
                <div>4. <strong style={{color:T.textPri}}>Load selectively</strong> — use usecols= and dtype= in read_csv()</div>
                <div>5. <strong style={{color:T.textPri}}>Use .loc[] for assignment</strong> — never chained indexing (df[mask]["col"] = val)</div>
                <div>6. <strong style={{color:T.textPri}}>For 100GB+</strong> — use Dask or Polars instead of Pandas</div>
              </div>
            </InfoBox>
            <CodeBlock code={`# eval() for computed columns (compiled — faster than Python)
import pandas as pd
df = pd.DataFrame({"a": range(100000), "b": range(100000)})
df.eval("c = a + b * 2", inplace=True)

# Efficient string ops — use .str, never apply(str.method)
# SLOW: df["col"].apply(lambda x: x.lower())
# FAST: df["col"].str.lower()

# Avoid SettingWithCopyWarning
# WRONG: df[df.cpu > 80]["status"] = "high"  # may not update df!
# RIGHT: df.loc[df.cpu > 80, "status"] = "high"

# Use query() for complex multi-condition filters
# Compiled internally — often faster than Boolean indexing
df.query("cpu_pct > 80 and status == 'warn'")`} />
            <Exercises items={[
              "Load a 1M-row metrics CSV. Profile memory before and after dtype optimization. Target 50% reduction",
              "Benchmark: loop vs apply() vs np.where for conditional operation on 500K rows",
            ]} />
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTION 14: ADVANCED TOPICS ───────────────────────────────────────────────
function S14() {
  return (
    <Tabs tabs={[
      {
        label:"MultiIndex",
        content:(
          <div>
            <Concept>A MultiIndex (hierarchical index) has multiple levels of row or column labels. Essential for time series per group — e.g., hourly metrics per cluster per region.</Concept>
            <ASCII>{`MultiIndex structure:
                     cpu   memory
(region, cluster)
(mh,     mh-01)    72.3     256   ← level 0: region, level 1: cluster
(mh,     mh-02)    85.1     128
(dl,     dl-01)    45.6     512
(dl,     dl-02)    91.2    1024`}</ASCII>
            <CodeBlock code={`import pandas as pd

arrays = [["mh","mh","dl","dl"], ["mh-01","mh-02","dl-01","dl-02"]]
idx = pd.MultiIndex.from_arrays(arrays, names=["region","cluster"])

df = pd.DataFrame({"cpu":[72.3,85.1,45.6,91.2],"mem":[256,128,512,1024]}, index=idx)
print(df)

# loc by MultiIndex level
print(df.loc["mh"])               # all mh clusters
print(df.loc[("mh","mh-01")])     # specific cluster

# xs (cross-section) — cleaner syntax
print(df.xs("mh", level="region"))

# Reset to flat index
print(df.reset_index())`} />
          </div>
        )
      },
      {
        label:"Window Functions",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd, numpy as np

df = pd.DataFrame({
    "hour": range(8),
    "cpu":  [72, 80, 85, 90, 88, 75, 70, 65]
})

# Rolling (sliding window)
df["roll_avg"] = df["cpu"].rolling(3).mean()
df["roll_std"] = df["cpu"].rolling(3).std()

# Expanding (cumulative from start)
df["cum_max"] = df["cpu"].expanding().max()
df["cum_avg"] = df["cpu"].expanding().mean()

# Exponential weighted
df["ewma"] = df["cpu"].ewm(span=3, adjust=False).mean()

# Bollinger bands
df["upper"] = df["roll_avg"] + 2 * df["roll_std"]
df["lower"] = df["roll_avg"] - 2 * df["roll_std"]
df["spike"] = df["cpu"] > df["upper"]

print(df[["hour","cpu","roll_avg","upper","spike"]].round(1))`} />
            <OutputBox>{`   hour  cpu  roll_avg  upper  spike
0     0   72       NaN    NaN  False
1     1   80       NaN    NaN  False
2     2   85      79.0   92.1  False
3     3   90      85.0   95.2  False
4     4   88      87.7   95.5  False
5     5   75      84.3   97.1  False
6     6   70      77.7   90.0  False
7     7   65      70.0   83.7  False`}</OutputBox>
          </div>
        )
      },
      {
        label:"Explode & Chaining",
        content:(
          <div>
            <CodeBlock code={`import pandas as pd

# explode() — one row per list element
df = pd.DataFrame({
    "cluster": ["mh-01", "dl-01"],
    "pods":    [["nginx","redis","kafka"], ["postgres","mongo"]]
})
print(df.explode("pods").reset_index(drop=True))

# ── Method Chaining — build readable pipelines ──
import numpy as np

df_raw = pd.DataFrame({
    "cluster": ["mh-01","mh-01","dl-01","dl-01","mh-02",None],
    "cpu":     [72.3, 72.3, 45.6, 91.2, 85.1, 60.0],
    "status":  ["ok","ok","ok","crit","warn","ok"]
})

result = (
    df_raw
    .dropna(subset=["cluster"])
    .drop_duplicates()
    .assign(cpu_tier=lambda x: pd.cut(
        x["cpu"],
        bins=[0,50,80,100],
        labels=["low","mid","high"]
    ))
    .query("status != 'ok'")
    .groupby("cluster")
    .agg(
        avg_cpu   = ("cpu",    "mean"),
        alert_cnt = ("status", "count")
    )
    .sort_values("avg_cpu", ascending=False)
    .reset_index()
)
print(result)`} />
            <OutputBox>{`  cluster      pods
0   mh-01     nginx
1   mh-01     redis
2   mh-01     kafka
3   dl-01  postgres
4   dl-01     mongo

  cluster  avg_cpu  alert_cnt
0   dl-01     91.2          1
1   mh-02     85.1          1`}</OutputBox>
            <InterviewQ items={[
              {q:"When would you use Polars or Dask instead of Pandas?", a:"Polars for single-node fast operations on 1–50GB (multi-threaded, lazy evaluation, Rust-based). Dask for distributed computation on 100GB+. Pandas is ideal for 0–5GB where iteration speed and ecosystem integration (sklearn, matplotlib) matter most."},
              {q:"Explain the split-apply-combine pattern.", a:"groupby() splits the DataFrame into groups. Each group gets the apply() function run on it independently. Results are combined back into a single output. It's the DataFrame equivalent of SQL GROUP BY, but with arbitrary Python functions."},
              {q:"What causes SettingWithCopyWarning?", a:"Chained indexing: df[mask]['col'] = val. This may operate on a copy, not the original. Fix: use df.loc[mask, 'col'] = val which always modifies the original in-place."},
            ]} />
            <Exercises items={[
              "Build a full pipeline: load Prometheus CSV → clean nulls → filter by region → groupby → rolling stats → export Parquet",
              "Create a MultiIndex DataFrame with (namespace, pod) as index. Query all pods in 'prod' namespace",
              "Given a column of JSON label strings, parse each with json.loads, normalize, and join back to the DataFrame",
              "Implement Bollinger band anomaly detection on 1-hour cpu_pct data. Count spike events per cluster",
            ]} />
            <div style={{background:"linear-gradient(135deg, #0F1E2E 0%, #071929 100%)",
              border:`1px solid ${T.accent}22`,borderRadius:12,padding:24,textAlign:"center",marginTop:8}}>
              <div style={{fontSize:32,marginBottom:12}}>🎓</div>
              <div style={{fontSize:16,fontWeight:600,color:T.textPri,marginBottom:8}}>Course Complete!</div>
              <div style={{fontSize:13,color:T.textSec,lineHeight:1.7,maxWidth:480,margin:"0 auto"}}>
                You've covered all 14 sections — from <code style={{color:T.accent}}>pd.DataFrame(&#123;&#125;)</code> to
                production-grade method chaining. The best next step: build a real project.
                Try analyzing a Prometheus metrics TSDB export or a Kubernetes event log
                with everything you've learned.
              </div>
            </div>
          </div>
        )
      },
    ]} />
  );
}

// ─── SECTIONS METADATA ────────────────────────────────────────────────────────
const SECTIONS = [
  {id:"home", icon:"🏠", title:"Overview",             sub:"Start here",                    component:null},
  {id:"s1",  icon:"🐼", title:"DataFrame Fundamentals",sub:"What is Pandas, Series, creating DFs", component:S1},
  {id:"s2",  icon:"🔍", title:"Exploration",           sub:"head, info, describe, memory",  component:S2},
  {id:"s3",  icon:"🎯", title:"Selecting Data",        sub:"loc, iloc, Boolean, query()",   component:S3},
  {id:"s4",  icon:"🧹", title:"Data Cleaning",         sub:"Nulls, duplicates, dtypes",     component:S4},
  {id:"s5",  icon:"⚡", title:"Transformation",        sub:"apply, map, lambda, str, dt",   component:S5},
  {id:"s6",  icon:"📊", title:"Sorting & Ranking",     sub:"sort_values, nlargest, rank",   component:S6},
  {id:"s7",  icon:"∑",  title:"Aggregation",           sub:"sum, mean, value_counts",       component:S7},
  {id:"s8",  icon:"🗂", title:"GroupBy Deep Dive",     sub:"groupby, transform, filter",    component:S8},
  {id:"s9",  icon:"🔗", title:"Combining DataFrames",  sub:"concat, merge, join",           component:S9},
  {id:"s10", icon:"↔", title:"Pivot & Reshape",        sub:"pivot_table, melt, stack",      component:S10},
  {id:"s11", icon:"💾", title:"Working with Files",    sub:"CSV, Excel, JSON, Parquet",     component:S11},
  {id:"s12", icon:"⏱", title:"Time Series",           sub:"resample, rolling, dt ops",     component:S12},
  {id:"s13", icon:"🚀", title:"Performance",           sub:"vectorize, memory, categoricals",component:S13},
  {id:"s14", icon:"🏆", title:"Advanced Topics",       sub:"MultiIndex, chaining, explode", component:S14},
];

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
function HomePage({ onNavigate }) {
  return (
    <div>
      <div style={{marginBottom:28}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:2,
          textTransform:"uppercase",marginBottom:10}}>Complete Learning Roadmap</div>
        <h1 style={{fontSize:26,fontWeight:700,color:T.textPri,margin:"0 0 10px",lineHeight:1.3}}>
          Pandas DataFrames<br />
          <span style={{color:T.accent}}>Beginner → Advanced</span>
        </h1>
        <p style={{fontSize:14,color:T.textSec,lineHeight:1.7,maxWidth:560}}>
          14 structured sections covering every DataFrame operation with real-world examples
          from Kubernetes, Prometheus, cloud ops, and ML. Every topic includes analogies,
          ASCII diagrams, code examples with output, interview questions, and hands-on exercises.
        </p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:28}}>
        {[
          {num:"14",label:"Sections"},
          {num:"50+",label:"Topics"},
          {num:"100+",label:"Code Examples"},
          {num:"60+",label:"Exercises"},
        ].map((stat,i)=>(
          <div key={i} style={{background:T.card,borderRadius:10,padding:"12px 14px",
            border:`1px solid ${T.border}`,textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:700,color:T.accent}}>{stat.num}</div>
            <div style={{fontSize:11,color:T.textMute,marginTop:2}}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {SECTIONS.filter(s=>s.id!=="home").map((s,i)=>(
          <button key={s.id} onClick={()=>onNavigate(s.id)}
            style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
              padding:"12px 14px",cursor:"pointer",textAlign:"left",
              transition:"all .15s",fontFamily:"inherit",display:"flex",gap:12,alignItems:"flex-start"}}>
            <span style={{fontSize:18,lineHeight:1.2,minWidth:24}}>{s.icon}</span>
            <div>
              <div style={{fontSize:11,color:T.textMute,marginBottom:2}}>Section {i+1}</div>
              <div style={{fontSize:13,fontWeight:600,color:T.textPri,marginBottom:2}}>{s.title}</div>
              <div style={{fontSize:11,color:T.textSec}}>{s.sub}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function PythonPandas() {
  const [current, setCurrent] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef(null);

  const navigate = (id) => {
    setCurrent(id);
    setVisited(prev => new Set([...prev, id]));
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const currentIdx = SECTIONS.findIndex(s=>s.id===current);
  const prevSection = currentIdx > 0 ? SECTIONS[currentIdx-1] : null;
  const nextSection = currentIdx < SECTIONS.length-1 ? SECTIONS[currentIdx+1] : null;
  const CurrentComponent = SECTIONS[currentIdx]?.component;
  const progressPct = Math.round((visited.size / SECTIONS.length) * 100);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 70px)",
      background:T.bg,color:T.textPri,fontFamily:"system-ui,-apple-system,sans-serif",overflow:"hidden"}}>

      {/* TOP BAR */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",
        background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0,zIndex:10}}>
        <button onClick={()=>setSidebarOpen(o=>!o)}
          style={{background:"none",border:"none",color:T.textSec,cursor:"pointer",
            fontSize:18,padding:"2px 4px",lineHeight:1}}>☰</button>
        <span style={{fontSize:15,fontWeight:600,color:T.textPri}}>🐼 Pandas Roadmap</span>
        <div style={{flex:1,height:4,background:T.card,borderRadius:2,overflow:"hidden"}}>
          <div style={{width:`${progressPct}%`,height:"100%",background:T.accent,
            borderRadius:2,transition:"width .4s ease"}} />
        </div>
        <span style={{fontSize:12,color:T.accent,minWidth:36,textAlign:"right",fontWeight:600}}>
          {progressPct}%
        </span>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>

        {/* SIDEBAR */}
        {sidebarOpen && (
          <div style={{width:220,minWidth:220,background:T.surface,
            borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
            <div style={{padding:"8px 6px"}}>
              {SECTIONS.map((s,i)=>(
                <button key={s.id} onClick={()=>navigate(s.id)}
                  style={{
                    display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",border:"none",borderRadius:7,cursor:"pointer",
                    background:current===s.id ? `${T.accent}18` : "none",
                    fontFamily:"inherit",marginBottom:2,
                    borderLeft:current===s.id ? `2px solid ${T.accent}` : "2px solid transparent",
                  }}>
                  <span style={{fontSize:13,minWidth:18}}>{s.icon}</span>
                  <div style={{flex:1,textAlign:"left"}}>
                    <div style={{fontSize:11,color:current===s.id?T.accent:visited.has(s.id)?T.green:T.textSec,
                      fontWeight:current===s.id?600:400,lineHeight:1.3}}>
                      {s.id==="home" ? s.title : `${i}. ${s.title}`}
                    </div>
                  </div>
                  {visited.has(s.id) && current!==s.id && (
                    <span style={{fontSize:10,color:T.green}}>✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MAIN CONTENT */}
        <div ref={contentRef} style={{flex:1,overflowY:"auto"}}>
          <div style={{maxWidth:900,margin:"0 auto",padding:"24px 28px"}}>
          {current==="home" ? (
            <HomePage onNavigate={navigate} />
          ) : (
            <div>
              {/* Section header */}
              <div style={{marginBottom:24}}>
                <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:1.5,
                  textTransform:"uppercase",marginBottom:8}}>
                  Section {currentIdx} of {SECTIONS.length-1}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <span style={{fontSize:24}}>{SECTIONS[currentIdx].icon}</span>
                  <h1 style={{fontSize:22,fontWeight:700,color:T.textPri,margin:0}}>
                    {SECTIONS[currentIdx].title}
                  </h1>
                </div>
                <p style={{fontSize:13.5,color:T.textSec,margin:0,lineHeight:1.6}}>
                  {SECTIONS[currentIdx].sub}
                </p>
              </div>

              {/* Section content */}
              {CurrentComponent && <CurrentComponent />}

              {/* Navigation footer */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                marginTop:32,paddingTop:20,borderTop:`1px solid ${T.border}`}}>
                <button onClick={()=>prevSection && navigate(prevSection.id)}
                  disabled={!prevSection}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",
                    background:prevSection?T.card:"transparent",border:`1px solid ${prevSection?T.border:"transparent"}`,
                    borderRadius:8,color:prevSection?T.textSec:T.textMute,cursor:prevSection?"pointer":"default",
                    fontSize:13,fontFamily:"inherit",transition:"all .15s"}}>
                  ← {prevSection ? prevSection.title : ""}
                </button>
                {nextSection ? (
                  <button onClick={()=>navigate(nextSection.id)}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",
                      background:T.accent,border:"none",borderRadius:8,color:"#0B1426",
                      cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",
                      transition:"all .15s"}}>
                    {nextSection.title} →
                  </button>
                ) : (
                  <span style={{fontSize:13,color:T.green,fontWeight:600}}>🎉 All sections complete!</span>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
